import { useState, useEffect } from 'react';
import {
  FaCalendarAlt, FaPlus, FaTimes, FaCheck, FaClock, FaBan,
  FaChevronDown, FaExclamationCircle,
} from 'react-icons/fa';
import api from '../../api';

const LEAVE_TYPES = ['Vacation', 'Sick', 'Personal', 'Bereavement', 'Unpaid', 'Other'];

const STATUS_STYLES = {
  pending:  'text-amber-600 bg-amber-50 border border-amber-200',
  approved: 'text-emerald-600 bg-emerald-50 border border-emerald-200',
  rejected: 'text-red-600 bg-red-50 border border-red-200',
};
const STATUS_ICONS = {
  pending:  <FaClock size={10} />,
  approved: <FaCheck size={10} />,
  rejected: <FaBan size={10} />,
};

function countWeekdays(start, end) {
  let count = 0;
  const cur = new Date(start);
  const last = new Date(end);
  while (cur <= last) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export default function LeaveRequest() {
  const [leaves, setLeaves]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const [form, setForm] = useState({
    type: 'Vacation', startDate: '', endDate: '', reason: '',
  });

  const days = form.startDate && form.endDate
    ? countWeekdays(form.startDate, form.endDate)
    : 0;

  useEffect(() => {
    api.getLeaves()
      .then(setLeaves)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (days <= 0) return setError('End date must be on or after start date.');
    setSubmitting(true);
    setError('');
    try {
      const leave = await api.submitLeave({ ...form, days });
      setLeaves(prev => [leave, ...prev]);
      setShowForm(false);
      setForm({ type: 'Vacation', startDate: '', endDate: '', reason: '' });
      setSuccess('Leave request submitted successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await api.cancelLeave(id);
      setLeaves(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const fmt = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : '—';

  return (
    <div className="bg-slate-50 min-h-screen">

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-5xl mx-auto px-4 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FaCalendarAlt size={20} /> Leave Requests
            </h1>
            <p className="text-blue-200 text-sm mt-1">Submit and track your time-off requests</p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setError(''); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl text-sm transition-colors"
          >
            {showForm ? <><FaTimes size={13} /> Cancel</> : <><FaPlus size={13} /> New Request</>}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {success && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm">
            <FaCheck size={13} /> {success}
          </div>
        )}

        {/* Submit Form */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold text-[#0c1b4d] mb-5 flex items-center gap-2">
              <FaPlus size={14} /> New Leave Request
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Leave Type</label>
                  <div className="relative">
                    <select
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      {LEAVE_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                    <FaChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Days preview */}
                <div className="flex items-end">
                  <div className="w-full bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 text-center">
                    <span className="text-2xl font-bold text-[#0c1b4d]">{days}</span>
                    <span className="text-xs text-blue-500 ml-1">business day{days !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Start date */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Start Date</label>
                  <input
                    type="date"
                    required
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>

                {/* End date */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">End Date</label>
                  <input
                    type="date"
                    required
                    value={form.endDate}
                    min={form.startDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Reason <span className="text-gray-400 normal-case font-normal">(optional)</span></label>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Briefly describe the reason for your request…"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
                  <FaExclamationCircle size={13} /> {error}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting || days <= 0}
                  className="px-5 py-2 text-sm bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-50">
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Requests list */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-bold text-[#0c1b4d] flex items-center gap-2">
              <FaCalendarAlt size={14} /> My Requests
            </h2>
          </div>

          {loading && (
            <div className="px-6 py-10 text-center text-gray-400 text-sm">Loading…</div>
          )}

          {!loading && leaves.length === 0 && (
            <div className="px-6 py-10 text-center text-gray-400 text-sm">
              No leave requests yet. Click <strong>New Request</strong> to get started.
            </div>
          )}

          {!loading && leaves.length > 0 && (
            <div className="divide-y divide-gray-50">
              {leaves.map(l => (
                <div key={l.id} className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{l.type}</span>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[l.status] || ''}`}>
                        {STATUS_ICONS[l.status]} {l.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {fmt(l.start_date)} – {fmt(l.end_date)} · {l.days} day{l.days !== 1 ? 's' : ''}
                    </p>
                    {l.reason && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-sm">{l.reason}</p>}
                    {l.reviewer_note && (
                      <p className="text-xs mt-1 text-gray-500 italic">
                        <span className="font-medium not-italic text-gray-600">Note:</span> {l.reviewer_note}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-400">
                      {new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {l.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(l.id)}
                        className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <FaTimes size={10} /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
